import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Save, PenLine } from "lucide-react";
import api, { formatError } from "../lib/api";
import { uploadFile } from "../lib/uploads";
import { Button } from "./ui/button";

const POSITIONS = [
  ["top-right", "Top right"],
  ["top-left", "Top left"],
  ["bottom-right", "Bottom right"],
  ["bottom-left", "Bottom left"],
  ["below-table", "Below totals"],
];

function SignaturePreview({ company, version }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!company.has_signature) { setSrc(""); return undefined; }
    let url = "";
    api.get(`/companies/${company.id}/signature`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [company.id, company.has_signature, version]);
  if (!src) return <span className="text-[11px] text-slate-400">No signature</span>;
  return <img src={src} alt="signature" className="h-10 max-w-[140px] object-contain border border-slate-200 rounded bg-white" data-testid={`co-signature-img-${company.id}`} />;
}

function CompanyCard({ c, onSaved }) {
  const [f, setF] = useState({ code_prefix: c.code_prefix, code_position: c.code_position, gst_no: c.gst_no, terms: c.terms });
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/companies/${c.id}/quote-settings`, { ...f, ...extra });
      toast.success(`${c.name} saved`);
      onSaved(data);
      setVer((v) => v + 1);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const onSig = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Signature must be an image (PNG/JPG)"); return; }
    try { const up = await uploadFile(file, { purpose: "company_signature", ref_id: c.id }); await save({ signature_upload_id: up.id }); }
    catch (err) { toast.error(err.message || "Upload failed"); }
  };
  const inp = "h-8 w-full rounded-md border border-slate-300 px-2 text-[12px] bg-white dark:bg-slate-900 dark:border-slate-700";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 dark:bg-slate-900 dark:border-slate-700" data-testid={`co-quote-card-${c.id}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold text-slate-800 dark:text-slate-100">{c.name}</div>
        <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${c.has_letterhead ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{c.has_letterhead ? "Letterhead ✓" : "No letterhead"}</span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <label className="col-span-2">Quote Code Position on PDF
          <select value={f.code_position} onChange={(e) => set("code_position", e.target.value)} className={`${inp} mt-1`} data-testid={`co-position-${c.id}`}>
            {POSITIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <label className="col-span-2">Company GST No.<input value={f.gst_no} onChange={(e) => set("gst_no", e.target.value.toUpperCase())} maxLength={15} className={`${inp} mono mt-1`} data-testid={`co-gst-${c.id}`} /></label>
        <label className="col-span-2">Terms &amp; Conditions
          <textarea value={f.terms} onChange={(e) => set("terms", e.target.value)} rows={4} placeholder={"1. Prices valid for 30 days\n2. Delivery within 15 working days"} className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-[12px] bg-white dark:bg-slate-900 dark:border-slate-700" data-testid={`co-terms-${c.id}`} />
        </label>
      </div>
      <div className="mt-2 text-[10.5px] text-slate-500">Every generated quote gets a unique 9-character code (e.g. <span className="mono">K7Q2M9XA4</span>) printed at this position.</div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <SignaturePreview company={c} version={ver} />
          <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50" data-testid={`co-signature-upload-${c.id}`}>
            <PenLine size={11} /> {c.has_signature ? "Replace" : "Upload"} Signature
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onSig} data-testid={`co-signature-input-${c.id}`} />
          </label>
        </div>
        <Button size="sm" onClick={() => save()} disabled={busy} className="h-8 bg-slate-900 hover:bg-slate-800 text-white" data-testid={`co-save-${c.id}`}><Save size={12} className="mr-1" /> {busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

export default function CompanyQuoteSettings() {
  const [companies, setCompanies] = useState([]);
  useEffect(() => { api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {}); }, []);
  const onSaved = (d) => setCompanies((p) => p.map((c) => (c.id === d.id ? d : c)));
  return (
    <div className="mt-6" data-testid="co-quote-settings">
      <div className="mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider text-slate-600"><Building2 size={14} /> Quote Settings per Company</div>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {companies.map((c) => <CompanyCard key={c.id} c={c} onSaved={onSaved} />)}
      </div>
    </div>
  );
}
